import React, { useEffect } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

export default function PropertyTypesPage() {
  // Set document head using React 19 features
  useEffect(() => {
    document.title = 'Property Types We Buy - Apartment Buildings, Multifamily Properties | SellApartmentsCash.com';
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'We buy all types of multifamily properties: apartment buildings, duplexes, triplexes, fourplexes, commercial buildings, student housing, senior housing, and distressed properties. Get cash offers for any condition.');

    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'apartment buildings for sale, multifamily properties, duplex for sale, triplex for sale, fourplex for sale, commercial buildings, student housing, senior housing');

    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', 'https://sellapartmentscash.com/property-types');
  }, []); // Empty dependency array ensures this only runs once

  return (
    <div className="font-inter">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-24">
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
          <div className="absolute inset-0 opacity-20">
            <img 
              src="https://public.readdy.ai/ai/img_res/8a749bc36ff90f55b17aaa6a03db6366.jpg"
              alt="Property types we buy"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="container mx-auto px-6 relative z-10">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-center">
              Property Types We Buy for Cash
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto text-center">
              From small duplexes to large apartment complexes - we buy all types of multifamily properties in any condition, any location.
            </p>
          </div>
        </section>

        {/* Property Types Grid */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-navy-900 mb-4">
                All Multifamily Property Types
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We purchase all types of income-producing multifamily properties across the United States. No matter the size, condition, or location.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Apartment Buildings */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-64">
                  <img 
                    src="https://readdy.ai/api/search-image?query=modern%20large%20apartment%20building%20complex%20with%20multiple%20floors%20and%20units%20professional%20real%20estate%20photography%20clean%20architecture%20residential%20property&width=400&height=300&seq=apartment-buildings-type&orientation=landscape"
                    alt="Apartment Buildings We Buy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-navy-900 mb-3">Apartment Buildings</h3>
                  <p className="text-gray-600 mb-4">
                    From 5-unit buildings to 100+ unit complexes. We buy apartment buildings in any condition, any occupancy level.
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• 5+ unit buildings</li>
                    <li>• Garden style complexes</li>
                    <li>• High-rise buildings</li>
                    <li>• Distressed properties</li>
                  </ul>
                </div>
              </div>

              {/* Duplexes */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-64">
                  <img 
                    src="https://readdy.ai/api/search-image?query=duplex%20house%20two-unit%20residential%20building%20side%20by%20side%20units%20professional%20real%20estate%20photography%20suburban%20neighborhood%20property&width=400&height=300&seq=duplex-property-type&orientation=landscape"
                    alt="Duplexes We Buy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-navy-900 mb-3">Duplexes</h3>
                  <p className="text-gray-600 mb-4">
                    Two-unit properties perfect for investors. We buy duplexes whether owner-occupied or fully rented.
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Side-by-side units</li>
                    <li>• Stacked units</li>
                    <li>• Owner-occupied</li>
                    <li>• Investment properties</li>
                  </ul>
                </div>
              </div>

              {/* Triplexes */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-64">
                  <img 
                    src="https://readdy.ai/api/search-image?query=triplex%20house%20three-unit%20residential%20building%20multifamily%20property%20professional%20real%20estate%20photography%20suburban%20investment%20property&width=400&height=300&seq=triplex-property-type&orientation=landscape"
                    alt="Triplexes We Buy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-navy-900 mb-3">Triplexes</h3>
                  <p className="text-gray-600 mb-4">
                    Three-unit buildings offering great rental income potential. We purchase triplexes in any condition.
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Three separate units</li>
                    <li>• Mixed occupancy OK</li>
                    <li>• Renovation needed OK</li>
                    <li>• Tenant issues OK</li>
                  </ul>
                </div>
              </div>

              {/* Fourplexes */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-64">
                  <img 
                    src="https://readdy.ai/api/search-image?query=fourplex%20house%20four-unit%20residential%20building%20multifamily%20investment%20property%20professional%20real%20estate%20photography%20suburban%20rental%20property&width=400&height=300&seq=fourplex-property-type&orientation=landscape"
                    alt="Fourplexes We Buy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-navy-900 mb-3">Fourplexes</h3>
                  <p className="text-gray-600 mb-4">
                    Four-unit properties maximizing rental income on smaller lots. We buy fourplexes regardless of vacancy rates.
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Four rental units</li>
                    <li>• High income potential</li>
                    <li>• Any vacancy level</li>
                    <li>• Management problems OK</li>
                  </ul>
                </div>
              </div>

              {/* Commercial Buildings */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-64">
                  <img 
                    src="https://readdy.ai/api/search-image?query=commercial%20building%20mixed-use%20property%20with%20residential%20units%20above%20retail%20professional%20real%20estate%20photography%20urban%20commercial%20property&width=400&height=300&seq=commercial-building-type&orientation=landscape"
                    alt="Commercial Buildings We Buy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-navy-900 mb-3">Commercial Buildings</h3>
                  <p className="text-gray-600 mb-4">
                    Mixed-use and commercial properties with residential components. We buy buildings with retail and apartments.
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Mixed-use properties</li>
                    <li>• Retail + apartments</li>
                    <li>• Office + residential</li>
                    <li>• Conversion opportunities</li>
                  </ul>
                </div>
              </div>

              {/* Student Housing */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-64">
                  <img 
                    src="https://readdy.ai/api/search-image?query=student%20housing%20apartment%20building%20near%20university%20campus%20modern%20residential%20property%20for%20college%20students%20professional%20real%20estate%20photography&width=400&height=300&seq=student-housing-type&orientation=landscape"
                    alt="Student Housing We Buy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-navy-900 mb-3">Student Housing</h3>
                  <p className="text-gray-600 mb-4">
                    Purpose-built or converted student housing near colleges and universities. We understand the unique challenges.
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Near college campuses</li>
                    <li>• High turnover properties</li>
                    <li>• Seasonal vacancy OK</li>
                    <li>• Wear and tear expected</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Property Conditions We Buy */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-navy-900 mb-4">
                Any Condition, Any Situation
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We specialize in purchasing properties that other buyers won't touch. No repairs needed, no hassles.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-hammer-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="font-bold text-navy-900 mb-2">Needs Repairs</h3>
                <p className="text-gray-600 text-sm">Properties requiring major renovations or capital improvements</p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-group-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="font-bold text-navy-900 mb-2">Tenant Problems</h3>
                <p className="text-gray-600 text-sm">Difficult tenants, evictions, or high vacancy rates</p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-bank-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="font-bold text-navy-900 mb-2">Financial Distress</h3>
                <p className="text-gray-600 text-sm">Foreclosure, bankruptcy, or behind on payments</p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-time-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="font-bold text-navy-900 mb-2">Quick Sale Needed</h3>
                <p className="text-gray-600 text-sm">Inherited properties or need to sell quickly for any reason</p>
              </div>
            </div>
          </div>
        </section>

        {/* Why We Buy These Properties */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-navy-900 mb-6">
                  Why We Buy All Property Types
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  As experienced multifamily property investors, we understand the value in all types of properties. Our extensive network allows us to see opportunity where others see problems.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy-900 mb-1">Portfolio Diversification</h4>
                      <p className="text-gray-600">We buy different property types to diversify our investment portfolio</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy-900 mb-1">Value-Add Opportunities</h4>
                      <p className="text-gray-600">We specialize in improving properties to increase their value and income</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy-900 mb-1">Market Expertise</h4>
                      <p className="text-gray-600">Deep understanding of local markets and property values nationwide</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy-900 mb-1">Quick Closings</h4>
                      <p className="text-gray-600">Cash purchases allow us to close in 7-14 days on any property type</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="w-full h-96">
                <img 
                  src="https://readdy.ai/api/search-image?query=diverse%20portfolio%20of%20multifamily%20properties%20including%20apartment%20buildings%20duplexes%20and%20commercial%20properties%20professional%20real%20estate%20investment%20portfolio%20photography&width=600&height=400&seq=property-portfolio-types&orientation=landscape"
                  alt="Property portfolio we invest in"
                  className="w-full h-full object-cover rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Market Coverage */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-navy-900 mb-4">
                Nationwide Property Buyers
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We purchase multifamily properties in all major markets across the United States. Local market expertise, national reach.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <h3 className="text-2xl font-bold text-orange-600 mb-2">Major Metro Areas</h3>
                <p className="text-gray-600 mb-4">Active in all major metropolitan markets nationwide</p>
                <ul className="text-sm text-gray-600 text-left space-y-1">
                  <li>• New York Metro</li>
                  <li>• Los Angeles</li>
                  <li>• Chicago</li>
                  <li>• Houston</li>
                  <li>• Phoenix</li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <h3 className="text-2xl font-bold text-orange-600 mb-2">Secondary Markets</h3>
                <p className="text-gray-600 mb-4">Strong presence in growing secondary markets</p>
                <ul className="text-sm text-gray-600 text-left space-y-1">
                  <li>• Nashville</li>
                  <li>• Austin</li>
                  <li>• Tampa</li>
                  <li>• Charlotte</li>
                  <li>• Denver</li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <h3 className="text-2xl font-bold text-orange-600 mb-2">Emerging Markets</h3>
                <p className="text-gray-600 mb-4">Identifying opportunities in up-and-coming areas</p>
                <ul className="text-sm text-gray-600 text-left space-y-1">
                  <li>• Boise</li>
                  <li>• Raleigh</li>
                  <li>• Kansas City</li>
                  <li>• Columbus</li>
                  <li>• Salt Lake City</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-orange-600 to-orange-700 text-white">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold mb-4">
              Ready to Sell Your Property?
            </h2>
            <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
              Get a no-obligation cash offer for your multifamily property in 24 hours. Any type, any condition, any location.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-orange-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors whitespace-nowrap">
                Get Cash Offer
              </button>
              <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-orange-600 transition-colors whitespace-nowrap">
                Call (555) 123-4567
              </button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}